<script setup lang="ts">
import { useIsMutating, useQuery } from '@tanstack/vue-query'
import { creditNoteQueries } from '@/features/invoices/queries'
import CardContainer from '@/components/Card/card-container.vue'
import CardHeader from '@/components/Card/card-header.vue'
import InputSelect from '@/components/input-select.vue'
import { formatToMainCurrency } from '@/helpers/formatting'
import CardContent from '@/components/Card/card-content.vue'
import InvoiceSkeletonPlaceholderText from '@/features/invoices/components/partials/InvoiceSkeletonPlaceholderText.vue'
import { computed, ref, watchEffect } from 'vue'
import { objectWithKeys } from '@/helpers'
import InputNumber from '@/components/input-number.vue'
import ContentLoader from '@/components/content-loader.vue'
import { InvoiceStatusNumeric } from '@/features/invoices/constants'
import { watchEffectUntilStopped } from '@/shared/composables/watchEffectUntilStopped'
import { creditNoteDetailedSchema } from '@/features/invoices/models/schemas/credit-notes'

const emit = defineEmits<{ 'update:allocated': [{ error: boolean, amountInCents: number }] }>()
const props = defineProps<{ invoiceId: number, invoiceStatus: InvoiceStatusNumeric, invoiceAmountInCents: number }>()
const { data: applicableCreditNotes, isFetching } = useQuery(creditNoteQueries.allocatable(props.invoiceId))
const selectedCreditNoteIds = ref<number[]>([])
const allocatedAmountsInCents = ref<{ [creditNoteId: number]: number|undefined }>({})
const pendingMutationCount = useIsMutating()

watchEffectUntilStopped(
  () => {
    // Sometimes the mutation to update the credit notes is still running when this component is loaded (usually when
    // updating + reloading the invoice finishes before the credit notes are finished allocating). This is why we check
    // if there are any pending mutations.
    if (applicableCreditNotes.value === undefined || isFetching.value || pendingMutationCount.value > 0) {
      return
    }

    // Populate the initial value of the allocated amounts.
    allocatedAmountsInCents.value = Object.fromEntries(
      applicableCreditNotes.value.flatMap(
        creditNote => creditNote.allocations.flatMap(
          allocation => ({ ...allocation, creditNoteId: creditNote.id })
        )
      ).filter(
        x => x.invoiceId === props.invoiceId
      ).map(
        x => [x.creditNoteId, x.allocatedAmountInCents]
      )
    )

    // Populate the initial selected credit notes.
    selectedCreditNoteIds.value = applicableCreditNotes.value.filter(
      creditNote => creditNote.allocations.some(
        allocation => allocation.invoiceId === props.invoiceId
      )
    ).map(
      x => x.id
    )

    return true
  }
)

const isEditable = computed(() => props.invoiceStatus === InvoiceStatusNumeric.Draft)

const displayedAllocations = computed(
  () => (
    applicableCreditNotes.value?.filter(
      x => selectedCreditNoteIds.value.includes(x.id)
    )
    .map(
      creditNote => ({
        creditNoteId: creditNote.id,
        creditNoteName: creditNote.creditNoteReference,
        remainingBalanceInCents: creditNote.remainingAmountInCents,
      })
    ) ?? []
  )
)

const remainingAmountsInCentsByCreditNote = computed(
  () => (
    !isEditable.value
      ? { } as Record<number, number>
      : displayedAllocations.value.reduce(
        (acc, item) => {
          const { creditNoteId } = item

          if (!(creditNoteId in acc)) {
            acc[creditNoteId] = item.remainingBalanceInCents
          }
          acc[creditNoteId] -= allocatedAmountsInCents.value[item.creditNoteId] ?? 0

          return acc
        },
        { } as Record<number, number>
      )
  )
)

function updateAllocatedAmount(creditNoteId: number, amount?: number) {
  if (typeof amount === 'number' && !isNaN(amount) && amount > 0) {
    allocatedAmountsInCents.value[creditNoteId] = amount * 100
  } else {
    allocatedAmountsInCents.value[creditNoteId] = undefined
  }
}

const subTotalInCents = computed(
  () => displayedAllocations.value.map(
    x => allocatedAmountsInCents.value[x.creditNoteId] ?? 0
  ).reduce(
    (acc, value) => acc + value,
    0
  )
)

// Watch the remaining amounts, and update the error if anything is exceeded.
watchEffect(
  () => {
    let error = false

    // Some values are negative.
    if (Object.values(remainingAmountsInCentsByCreditNote.value).some(x => x < 0)) {
      error = true
    }
    // The amount allocated is greater than the invoice amount.
    else if (props.invoiceAmountInCents > 0 && subTotalInCents.value >= props.invoiceAmountInCents) {
      error = true
    }

    emit('update:allocated', { error, amountInCents: subTotalInCents.value })
  }
)

defineExpose({
  allocations() {
    return displayedAllocations.value.map(
      x => {
        const amount = allocatedAmountsInCents.value[x.creditNoteId]

        return typeof amount === 'number' && !isNaN(amount)
          ? { creditNoteId: x.creditNoteId, amountInCents: amount }
          : undefined
      }
    ).filter(
      x => x !== undefined
    )
  },
  clearAllocations() {
    allocatedAmountsInCents.value = { }
  }
})
</script>

<template>
  <CardContainer>
    <CardHeader :size="isEditable ? 'small' : 'normal'">
      <div class="d-flex align-items-center">
        <span class="text-nowrap">CREDIT NOTE</span>

        <InputSelect v-if="isEditable"
          class="ms-3"
          multiple
          v-model:selected="selectedCreditNoteIds"
          :items="(applicableCreditNotes ?? []).map(x => ({ value: x.id, text: `${x.creditNoteReference} | ${formatToMainCurrency(x.remainingAmountInCents / 100)} remaining`}))"
          item-text="text"
          item-value="value" />
      </div>
    </CardHeader>
    <CardContent>
      <table class="table table-no-bottom-border">
        <thead>
          <tr>
            <th>Credit Note No.</th>
            <th class="text-end">Remaining (R)</th>
            <th class="text-end">Credit Applied (R)</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="displayedAllocations.length > 0">
            <tr v-for="item in displayedAllocations" :key="item.creditNoteId">
              <td>{{ item.creditNoteName }}</td>
              <td class="text-end">{{ formatToMainCurrency(item.remainingBalanceInCents / 100) }}</td>
              <td class="text-end ps-4" style="width: 15%">
                <InputNumber v-if="isEditable" :value="allocatedAmountsInCents[item.creditNoteId] ? allocatedAmountsInCents[item.creditNoteId]! / 100 : undefined"
                             align="right"
                             @update:value="updateAllocatedAmount(item.creditNoteId, $event)"
                             placeholder="0.00"
                             error-variant="tooltip"
                             :error="
                               remainingAmountsInCentsByCreditNote[item.creditNoteId] < 0
                                 ? `You have allocated more than is remaining on this credit note.\nYou have allocated ${formatToMainCurrency(Math.abs(remainingAmountsInCentsByCreditNote[item.creditNoteId] / 100))} more than is available.`
                                 : undefined
                             "
                             class="text-end">
                  <template #prefix><span class="text-muted">R</span></template>
                </InputNumber>
                <span v-else>
                  {{
                    formatToMainCurrency(
                      allocatedAmountsInCents[item.creditNoteId] ? allocatedAmountsInCents[item.creditNoteId]! / 100 : undefined,
                      { showSymbol: false }
                    ) ?? '-'
                  }}
                </span>
              </td>
            </tr>
          </template>
          <tr v-else-if="isFetching || pendingMutationCount > 0">
            <td colspan="6">
              <ContentLoader loading variant="table" :cols="6" :rows="1" :table-header="false" />
            </td>
          </tr>
          <tr v-else>
            <td><InvoiceSkeletonPlaceholderText /></td>
            <td class="text-end"><InvoiceSkeletonPlaceholderText /></td>
            <td class="text-end"><InvoiceSkeletonPlaceholderText /></td>
          </tr>
        </tbody>
      </table>

      <div :class="$style.summary">
        <div class="text-end">Subtotal</div>
        <div>{{ formatToMainCurrency(subTotalInCents / 100) }}</div>
      </div>
    </CardContent>
  </CardContainer>
</template>

<style module lang="scss">
@import "@/assets/variables";

.summary {
  background: #eee;
  font-weight: 700;
  padding: 20px 20px 20px 64.5%;
  border-bottom-left-radius: #{$card-border-radius};
  border-bottom-right-radius: #{$card-border-radius};
  display: grid;
  grid-template-columns: 167px max-content;
  column-gap: 78px;
  margin: #{$card-spacer-y} #{-1 * $card-spacer-x} #{-1 * $card-spacer-y};
}
</style>
